import React, { useState, useEffect } from "react";
import { User } from "../../../../services/Dtos";
import { VStack, Box } from "@chakra-ui/react";
import { genders, roles } from "../../../../services/constants";
import useIcons from "../../../../hooks/useIcons";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import SelectInput from "../../../CustomUi/SelectInput";
import CustomAlert from "../../../CustomUi/CustomAlert";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useUserService } from "../../../../hooks/Domain/useUserService";
import InlineSeparator from "../../../CustomUi/InlineSeparator";
import { validateGender } from "../../../../services/validation";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userToEdit: User | null;
}

const UserModal: React.FC<Props> = ({ isOpen, onClose, userToEdit }) => {
  const {
    createUser,
    updateUser,
    deleteUser,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useUserService();

  const getInitialUserFormState = (): User => ({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    schoolId: "",
    gender: 0,
    role: 0,
    isActive: false,
    avatarUrl: "",
    createdAt: "",
    updatedAt: "",
    isAccountActivated: false,
    accountActivatedAt: "",
  });

  const initialUserFormState = getInitialUserFormState();

  const icons = useIcons();
  const [userFormState, setUserFormState] =
    useState<User>(initialUserFormState);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    firstName: { type: "text" as const, required: true, maxLength: 50 },
    lastName: { type: "text" as const, required: true, maxLength: 50 },
    email: { type: "email" as const, required: true },
    gender: {
      type: "select" as const,
      required: true,
      customValidation: (value: any) =>
        validateGender(parseInt(value)) ? "" : "Invalid gender",
    },
    role: { type: "select" as const, required: true },
  } as const;

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: userToEdit,
      currentData: userFormState,
      isEditMode: !!userToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setUserFormState,
    setTouchedFields,
    integerFields: ["gender", "role"],
  });

  // Real-time validation
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof User;
    return validateField(fieldKey, value);
  };

  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (userToEdit) {
      setUserFormState(userToEdit);
    } else {
      setUserFormState(getInitialUserFormState());
    }
    setTouchedFields(new Set());
  }, [userToEdit, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      userFormState[fieldName as keyof User]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      userFormState[fieldName as keyof User]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (userToEdit) {
      const response = await updateUser(userToEdit.id, userFormState);
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createUser(userFormState);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDelete = async () => {
    if (!userToEdit) return;
    const response = await deleteUser(userToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && userToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const genderOptions = genders.map((g) => ({
    label: g.name,
    value: g.value.toString(),
  }));

  const roleOptions = roles.map((r) => ({
    label: r.name,
    value: r.value.toString(),
  }));

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDelete}
        deleteMessage="All related user records and permissions will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={userToEdit ? "Edit User" : "Add New User"}
        icon={icons.people.user}
        mode={userToEdit ? "edit" : "add"}
        actionText={userToEdit ? "Save Changes" : "Save User"}
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
      >
        {errors.length > 0 && (
          <VStack gap={2} mb={4}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          {/* Personal Information Section */}
          <Box>
            <InlineSeparator label="Personal Information" />
            <VStack gap={4}>
              <TextInput
                label="First Name"
                name="firstName"
                type="text"
                value={userFormState.firstName}
                onChange={handleFieldChange}
                placeholder="First name"
                required
                error={
                  showFieldError("firstName") ? getFieldError("firstName") : ""
                }
                invalid={showFieldError("firstName")}
              />

              <TextInput
                label="Last Name"
                name="lastName"
                type="text"
                value={userFormState.lastName}
                onChange={handleFieldChange}
                placeholder="Last name"
                required
                error={
                  showFieldError("lastName") ? getFieldError("lastName") : ""
                }
                invalid={showFieldError("lastName")}
              />
            </VStack>
          </Box>

          {/* Contact and Role Information Section */}
          <Box>
            <InlineSeparator label="Contact and Role Information" />
            <VStack gap={4}>
              <TextInput
                label="Email"
                name="email"
                type="email"
                value={userFormState.email}
                onChange={handleFieldChange}
                placeholder="Email"
                required
                error={showFieldError("email") ? getFieldError("email") : ""}
                invalid={showFieldError("email")}
              />

              <SelectInput
                label="Gender"
                name="gender"
                value={userFormState.gender.toString() || ""}
                onChange={handleFieldChange}
                collection={genderOptions}
                placeholder="Select gender"
                required
                error={showFieldError("gender") ? getFieldError("gender") : ""}
                invalid={showFieldError("gender")}
              />

              <SelectInput
                label="Role"
                name="role"
                value={userFormState.role.toString() || ""}
                onChange={handleFieldChange}
                collection={roleOptions}
                placeholder="Select Role"
                required
                error={showFieldError("role") ? getFieldError("role") : ""}
                invalid={showFieldError("role")}
              />
            </VStack>
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default UserModal;
